/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, Chip, TextField } from '@mui/material';
import InputGroupV2 from '../InputGroupV2';
import { useAutocompleteTabSelect } from '../autocompleteTabSelect';
import { useAutocompleteListSearch } from '../useAutocompleteListSearch';
import { matchesTextByWordStart } from '../selectSearchFilter';

const getOptionLabel = (option) => {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  return option.label != null ? String(option.label) : '';
};

const isOptionEqualToValue = (a, b) => {
  if (a == null || b == null) return false;
  if (a.value !== undefined && b.value !== undefined) {
    return a.value === b.value;
  }
  return a === b;
};

const MultiSelectV2 = ({
  label,
  onChange,
  placement,
  title,
  useTooltip,
  isRequired,
  options,
  defaultValue,
  error,
  helperText,
  disabled,
}) => {
  const value = Array.isArray(defaultValue) ? defaultValue : [];
  const optionList = options || [];

  const matchFn = useCallback(
    (option, q) => matchesTextByWordStart(getOptionLabel(option), q),
    []
  );

  const {
    onInputChange,
    onClose,
    filterOptions,
    listboxSlotProps,
    noOptionsText,
  } = useAutocompleteListSearch({
    optionsCount: optionList.length,
    multiSelect: true,
    matchFn,
    getSortLabel: getOptionLabel,
    listMaxHeight: 200,
  });

  const handleFilterOptions = useCallback(
    (opts) => filterOptions(opts),
    [filterOptions]
  );

  const handleChange = (event, newValue) => {
    if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
      onChange(null);
    } else {
      onChange(newValue);
    }
  };

  const applyTabSelection = useCallback(
    (event, option) => {
      if (option == null) return;
      if (value.some((v) => isOptionEqualToValue(v, option))) {
        return;
      }
      onChange([...value, option]);
    },
    [value, onChange]
  );

  const { onHighlightChange, onKeyDown: onTabKeyDown } = useAutocompleteTabSelect(applyTabSelection);

  return (
    <InputGroupV2
      label={label}
      placement={placement}
      title={title}
      useTooltip={useTooltip}
      isRequired={isRequired}
    >
      <Autocomplete
        clearOnEscape
        disableCloseOnSelect
        disabled={disabled}
        filterOptions={handleFilterOptions}
        filterSelectedOptions={false}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        multiple
        noOptionsText={noOptionsText}
        options={optionList}
        value={value}
        onChange={handleChange}
        onClose={onClose}
        onHighlightChange={onHighlightChange}
        onInputChange={onInputChange}
        onKeyDown={onTabKeyDown}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={index}
              label={getOptionLabel(option)}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            fullWidth
            placeholder="Selecione..."
            sx={{
              '& .MuiInputBase-root': {
                bgcolor: disabled ? 'transparent' : 'background.paper',
              },
            }}
          />
        )}
        slotProps={{
          listbox: listboxSlotProps,
          popper: {
            sx: (theme) => ({
              zIndex: theme.zIndex.modal + 1,
            }),
          },
        }}
      />
      {error ? (
        helperText ? (
          helperText
        ) : (
          <Box
            component="p"
            sx={{
              m: 0,
              mt: 0.5,
              ml: 1.75,
              color: 'error.main',
              fontSize: 11,
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 400,
              letterSpacing: '0.33px',
              lineHeight: 1,
              minHeight: '1em',
              textAlign: 'left',
            }}
          >
            O campo é obrigatório
          </Box>
        )
      ) : null}
    </InputGroupV2>
  );
};

MultiSelectV2.propTypes = {
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  defaultValue: PropTypes.array.isRequired,
  placement: PropTypes.string,
  title: PropTypes.string,
  useTooltip: PropTypes.bool,
  isRequired: PropTypes.any,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
};

MultiSelectV2.defaultProps = {
  placement: '',
  title: '',
  useTooltip: false,
  isRequired: false,
  error: false,
  helperText: null,
  disabled: false,
};

export default MultiSelectV2;
